import React, { useState, useEffect } from "react";
// react component for creating dynamic tables
import ReactTable from "react-table";
import SweetAlert from "react-bootstrap-sweetalert";
import { useSelector, useDispatch } from "react-redux";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
// @material-ui/icons
import EditIcon from "@material-ui/icons/Edit";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import TransformIcon from "@material-ui/icons/Crop169";
import { trailersActions } from "../../redux/trailers/actions";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CustomInput from "components/CustomInput/CustomInput";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";
import CardHeader from "components/Card/CardHeader.js";

import { cardTitle } from "assets/jss/material-dashboard-pro-react.js";
import NewTruckDialog from "./NewTruckDialog";
import alertStyles from "assets/jss/material-dashboard-pro-react/views/sweetAlertStyle.js";

const styles = {
  cardIconTitle: {
    ...cardTitle,
    marginTop: "15px",
    marginBottom: "0px"
  },
  input: {
    paddingTop: 0
  }
};

const useStyles = makeStyles({
  ...styles,
  ...alertStyles
});

function filterCaseInsensitive(filter, row) {
  const id = filter.pivotId || filter.id;
  return row[id] !== undefined
    ? String(row[id].toLowerCase()).startsWith(filter.value.toLowerCase())
    : true;
}

export const TrailersManagement = () => {
  const dispatch = useDispatch();
  const [isNewTruckVisible, setNewTruckVisible] = useState(false);
  const [alert, setAlert] = useState(null);
  const [plateNumberState, setPlateNumberState] = useState("");
  const [trailer, setTrailer] = useState({});
  const { trailers, saving, savingSuccess, deleteSuccess } = useSelector(
    state => state.trailers
  );
  const [isEdit, setEdit] = useState(false);
  const [data, setData] = useState([]);

  useEffect(() => {
    dispatch(trailersActions.fetchTrailer());
  }, []);
  const onDataChanged = (key, value) => {
    setTrailer({
      ...trailer,
      [key]: value
    });

    if (key === "plate_number") {
      if (value.length >= 5) {
        setPlateNumberState("success");
      } else {
        setPlateNumberState("error");
      }
    }
  };

  const onEdit = trailer => {
    setEdit(true);
    setTrailer(trailer);
    setPlateNumberState("");
    setNewTruckVisible(true);
  };

  useEffect(() => {
    if (savingSuccess) {
      setNewTruckVisible(false);
      const action = isEdit ? "updated" : "added";
      setAlert(
        <SweetAlert
          success
          style={{ display: "block", marginTop: "-100px" }}
          title={`Trailer ${action}`}
          onConfirm={onDismissSave}
          onCancel={onDismissSave}
          confirmBtnCssClass={classes.button + " " + classes.success}
        >
          The Trailer has been {isEdit ? "updated" : "added"}.
        </SweetAlert>
      );
    }
  }, [savingSuccess]);

  useEffect(() => {
    if (deleteSuccess) {
      setAlert(
        <SweetAlert
          success
          style={{ display: "block", marginTop: "-100px" }}
          title="Deleted!"
          onConfirm={onDismissDelete}
          onCancel={onDismissDelete}
          confirmBtnCssClass={classes.button + " " + classes.success}
        >
          The Trailer has been deleted.
        </SweetAlert>
      );
    }
  }, [deleteSuccess]);

  const onDismissDelete = () => {
    setAlert(null);
    dispatch(trailersActions.resetSuccessDelete());
  };
  const onDismissSave = () => {
    setAlert(null);
    dispatch(trailersActions.resetSuccessSave());
  };

  const warningWithConfirmMessage = obj => {
    setAlert(
      <SweetAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title="Are you sure?"
        onConfirm={() => onDelete(obj)}
        onCancel={() => setAlert(null)}
        confirmBtnCssClass={classes.button + " " + classes.success}
        cancelBtnCssClass={classes.button + " " + classes.danger}
        confirmBtnText="Yes, delete it!"
        cancelBtnText="Cancel"
        showCancel
      >
        You will not be able to recover the trailer.
      </SweetAlert>
    );
  };

  const onDelete = obj => {
    dispatch(trailersActions.deleteTrailer(obj.id));
  };

  useEffect(() => {
    setData(
      trailers.map((prop, key) => {
        return {
          id: key,
          plate_number: prop.plate_number,
          color: prop.color || "N/A",
          brand: prop.brand || "N/A",
          actions: (
            // we've added some custom button actions
            <div className="actions-right">
              <Button
                justIcon
                round
                simple
                onClick={() => {
                  let trailer = trailers.find(o => o.id === prop.id);
                  onEdit(trailer);
                }}
                size="lg"
                color="primary"
                className="edit"
              >
                <EditIcon />
              </Button>{" "}
              {/* use this button to remove the data row */}
              <Button
                justIcon
                round
                simple
                onClick={() => {
                  const obj = trailers.find(o => o.id === prop.id);
                  warningWithConfirmMessage(obj);
                }}
                size="lg"
                color="danger"
                className="remove"
              >
                <DeleteForeverIcon />
              </Button>{" "}
            </div>
          )
        };
      })
    );
  }, [trailers]);

  const onSubmit = () => {
    if (plateNumberState !== "error") {
      dispatch(trailersActions.saveTrailer(trailer));
    } else {
      setPlateNumberState("error");
    }
  };

  const onOpenDialog = () => {
    setTrailer({
      plate_number: "",
      color: "",
      brand: ""
    });
    setPlateNumberState("");
    setNewTruckVisible(true);
    setEdit(false);
  };

  const classes = useStyles();

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Button
          onClick={onOpenDialog}
          style={{ float: "right", marginBottom: 10 }}
          size="lg"
          color="primary"
          variant="contained"
        >
          Add New Trailer
        </Button>
        <Card>
          <CardHeader color="primary" icon>
            <CardIcon color="primary">
              <TransformIcon />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>
              Trailer Management ({trailers.length})
            </h4>
          </CardHeader>
          <CardBody>
            <ReactTable
              FilterComponent={({ filter, onChange }) => (
                <CustomInput
                  value={filter ? filter.value : ""}
                  onChange={value => onChange(value)}
                  id="success"
                  // labelText="Plate number"
                  formControlProps={{
                    fullWidth: true,
                    className: classes.input
                  }}
                  inputProps={{
                    value: filter ? filter.value : ""
                  }}
                />
              )}
              data={data}
              filterable
              defaultFilterMethod={filterCaseInsensitive}
              columns={[
                {
                  Header: "Plate Number",
                  accessor: "plate_number"
                },
                {
                  Header: "Brand",
                  accessor: "brand"
                },
                {
                  Header: "Color",
                  accessor: "color"
                },
                {
                  Header: "Actions",
                  accessor: "actions",
                  sortable: false,
                  filterable: false
                }
              ]}
              defaultPageSize={10}
              showPaginationTop
              showPaginationBottom={false}
              className="-striped -highlight"
            />
          </CardBody>
        </Card>
      </GridItem>
      <NewTruckDialog
        title={trailer.id ? "Edit Trailer" : "Add new Trailer"}
        onClose={() => setNewTruckVisible(false)}
        onSubmit={onSubmit}
        onDataChanged={onDataChanged}
        data={trailer}
        plateNumberState={plateNumberState}
        isModalVisible={isNewTruckVisible}
        saving={saving}
      />
      {alert}
    </GridContainer>
  );
};

export default TrailersManagement;
